import type { i18n as I18nInstance } from 'i18next';
import { i18n } from '/shared/translations/i18n';

import type { QuestionConfigQuestionType } from '/index';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';
import formatNumber from '/shared/functions/formatNumber';

import truckIcon from '/business/shared/images/icons/bp-truck-articulated-small-pictograms-bp-green-.svg';
import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Upstream transportation card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Upstream transportation card image@2x.jpg';

import useGetTransportationAndDistributionSchema from '/business/shared/components/questions/scope3/transportAndDistribution/downstream/useGetTransportationAndDistributionSchema';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import educationContentJson from '/business/shared/components/questions/scope3/transportAndDistribution/upstream/_education/upstreamTransportationAndDistribution.json';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import useFormatUpstreamTransportationCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatUpstreamTransportationCustom';
import useFormatUpstreamTransportationSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatUpstreamTransportationSupplier';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import transportVehicleTypeLabel from '/business/shared/util/transportVehicleTypeLabel';
import { getTranslatedDisplayName } from '/business/shared/components/questions/helper/internals/mappedFieldValues';

const carVanTruckActivitySubheader = (
  <>
    <StyledParagraph $largeText $hasExtraMarginBottom>
      Enter the details for any inbound or outbound logistics services{' '}
      <b>paid for by your business and operated by another individual or business</b> during the reporting period:{' '}
      <ReportingPeriod $green />
    </StyledParagraph>
    <StyledInfoFlash>
      <div>For more information, refer to the "Help" section for detailed guidance.</div>
    </StyledInfoFlash>
  </>
);

export const getConfigureUpstreamTransportationAndDistribution = (
  _options: { i18n?: I18nInstance } = {},
): QuestionConfigQuestionType => {
  const activitySubquestions = {
    COMPANY_CARS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'cars',
      title: 'Upstream transportation and distribution - cars',
      breadcrumbTitle: 'Cars',
      apiKey: 'calculation-upstream-distrib-activity-car',
      useHookLoader: useGetTransportationAndDistributionSchema,

      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      table: {
        title: 'Calculated carbon emissions for activity data: ',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: [
          'calculation-upstream-distrib-activity-truck',
          'calculation-upstream-distrib-activity-van',
        ],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for upstream transportation and distribution: ',
        supplier: {
          columns: ['supplierName', 'supplierIntensityDataOrAbsoluteEmissions', 'totalDistance', 'carbonEmissions'],
        },
        custom: {
          columns: ['supplierName', 'intensityValue', 'unitOfMeasurement', 'totalDistanceQty', 'carbonEmissions'],
        },
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
    COMPANY_VANS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'vans',
      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      title: 'Upstream transportation and distribution - vans',
      breadcrumbTitle: 'Vans',
      apiKey: 'calculation-upstream-distrib-activity-van',
      useHookLoader: useGetTransportationAndDistributionSchema,

      table: {
        title: 'Calculated carbon emissions for activity data: ',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: [
          'calculation-upstream-distrib-activity-truck',
          'calculation-upstream-distrib-activity-car',
        ],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for upstream transportation and distribution: ',
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
    COMPANY_TRUCKS: {
      freeTypeDistance: true,
      displayLesseeField: true,
      id: 'trucks',
      initiative: {
        getVisuals: () => {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        },
      },
      title: 'Upstream transportation and distribution - trucks',
      breadcrumbTitle: 'Trucks',
      apiKey: 'calculation-upstream-distrib-activity-truck',
      useHookLoader: useGetTransportationAndDistributionSchema,
      table: {
        title: 'Calculated carbon emissions for activity data: ',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          'Supplier name',
          'Vehicle type',
          'Fuel type',
          'Vehicle size',
          'Total distance travelled',
          'Carbon emissions',
        ],
        cells: [
          'supplierName',
          transportVehicleTypeLabel,
          ({ fuelType }: { fuelType: string }) =>
            `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType}`, fuelType)}`,
          ({ vehicleSize }: { vehicleSize: string }) => {
            return getTranslatedDisplayName(
              vehicleSize,
              `business:emissionFactors.vehicleSizes.${vehicleSize}`,
              i18n.t,
            );
          },
          ({ totalDistance, distanceUnit }: { totalDistance: number; distanceUnit: string }) => {
            return <DistanceCanonicalName distance={totalDistance} unit={distanceUnit} type="annual" />;
          },
          ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: true,
        hasSiblingQuestions: true,
        siblingQuestionKeys: ['calculation-upstream-distrib-activity-van', 'calculation-upstream-distrib-activity-car'],
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for upstream transportation and distribution: ',
        supplier: {
          columns: ['supplierName', 'supplierIntensityDataOrAbsoluteEmissions', 'totalDistance', 'carbonEmissions'],
        },
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
      activitySubheader: carVanTruckActivitySubheader,
    },
  };
  return {
    title: 'Upstream transportation and distribution',
    id: 'upstream-transportation-and-distribution',
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamTrasportationAndDistribution',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamTrasportationAndDistributionTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.upstreamTrasportationAndDistribution',
    form: import(
      '/business/shared/components/questions/scope3/transportAndDistribution/shared/TransportAndDistributionVehSelector'
    ),
    scopeCategory: questionScopeCategories.SCOPE_3,
    subQuestions: [
      activitySubquestions.COMPANY_CARS,
      activitySubquestions.COMPANY_VANS,
      activitySubquestions.COMPANY_TRUCKS,
    ],
    icon: truckIcon,
    isCompletePropName: 'isUpstreamTransportationAndDistributionComplete',
    selector: {
      questionBaseRootApiKey: 'calculation-upstream-distrib-activity-car',
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: (
        <StyledParagraph $largeText>
          To calculate emissions associated with any inbound or outbound logistics services{' '}
          <b>paid for by your business and operated by another individual or business</b>, you can use a combination of
          activity data, supplier data, and custom data.
        </StyledParagraph>
      ),
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Supplier ID, type of fuel, vehicle size, total distance traveled.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData: 'Supplier name and intensity data/absolute emissions, total distance traveled.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Supplier name, intensity data/absolute emissions, total distance traveled.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatUpstreamTransportationSupplier({
        questionFormatName: 'upstream-transportation-and-distribution',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatUpstreamTransportationCustom({ questionFormatName: 'upstream-transportation-and-distribution' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the details for any inbound or outbound logistics services{' '}
          <b>paid for by your business and operated by another individual or business</b> during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: carVanTruckActivitySubheader,
  };
};
